import * as React from 'react';

import type { IconDefinition } from '../util';
import { BadgeContent } from './BadgeContent';
import { useBadge } from './useBadge';

export type BadgeKind =
    | 'primary'
    | 'secondary'
    | 'info'
    | 'danger'
    | 'warning'
    | 'merge'
    | 'upgrade'
    | 'done'
    | 'beta'
    | 'alpha'
    | 'ultimate';

export type BadgeSize = 'xsmall' | 'small' | 'medium';

export interface BadgeProps extends Omit<React.ComponentPropsWithoutRef<'div'>, 'onClick'> {
    icon?: IconDefinition;
    inverted?: boolean;
    iconOnly?: boolean;
    kind?: BadgeKind;
    /**
     * The label to show. Pass a number to get nice digit formatting.
     */
    label?: React.ReactNode;
    size?: BadgeSize;

    /**
     * Display in a slimmed down version
     */
    thin?: boolean;
}

/**
 * Badge should be for numerical, text, or iconical indicators and shouldn't have any behaviour expectations.
 */
export const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(
    function BadgeWithRef(props, ref) {
        const { className, icon, iconOnly, inverted, kind, label, size, thin, ...rest } = props;
        const { badgeProps, iconSize, isIconOnly, isNumber } = useBadge({
            className,
            icon,
            iconOnly,
            inverted,
            kind,
            label,
            size,
            thin,
        });

        return (
            <div ref={ref} {...badgeProps} {...rest}>
                <BadgeContent
                    {...props}
                    iconSize={iconSize}
                    isIconOnly={isIconOnly}
                    isNumber={isNumber}
                />
            </div>
        );
    }
);
